import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useCustomer from 'flow/hooks/useCustomer';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { isFlowFreeUser } from 'flow/utils/flow-utils';
import { canEditProfiles } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from './Page';

const NewProfile = () => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { ownCustomer } = useCustomer(extensionKey);
    const { extension } = useExtension(extensionKey);

    return (
        <Button
            color="primary"
            onClick={() =>
                openModal(
                    isFlowFreeUser(ownCustomer, extension)
                        ? 'createFlowFreeProfile'
                        : 'createProfile',
                    { extensionKey },
                )
            }
            iconKey="add"
            className="py-1"
            hideTextOnOnePanel={true}
        >
            {i18next.t('settings.profile.create_button')}
        </Button>
    );
};

const Profiles = () => {
    const { i18next } = useAppContext();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { goToSettings } = useSettingsNavigation();

    const { sortedProfiles } = useProfiles(extensionKey);
    const { isSinglePaneMode } = useSinglePaneMode();

    const [showKey, setShowKey] = useState(false);

    if (!sortedProfiles?.size) {
        return <Loading signalFinishedLoading={true} />;
    }

    return (
        <Page
            header="profilechooser.profiles"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
            action={canEditProfiles(sortedProfiles) && <NewProfile />}
        >
            <>
                {sortedProfiles.map((profile) => (
                    <RouteLink
                        key={profile.get('key')}
                        to={generatePath(
                            routes['settings.profiles.profileKey'],
                            { extensionKey, profileKey: profile.get('key') },
                        )}
                    >
                        {showKey && <span>{profile.get('plusId')}</span>}
                        {i18next.t(profile.get('description'))}
                    </RouteLink>
                ))}

                <Divider />

                <span>
                    <Button
                        color="ghost"
                        className="ring-0 forced-colors:outline-0"
                        onClick={() => setShowKey((showKey) => !showKey)}
                    >
                        {showKey
                            ? i18next.t('Settings.Profiles.Switch.Label.Hide')
                            : i18next.t('Settings.Profiles.Switch.Label')}
                    </Button>
                </span>
            </>
        </Page>
    );
};

export default Profiles;
